import React, { useState, memo } from 'react';
import { Check, X, ShieldCheck, Star, Target, ClipboardList, Smartphone, Palette, Flame, Calendar, Search, Trophy, Edit3, ChevronRight, ChevronLeft, ChevronDown, Quote, Clock, Zap, Brain, Activity, Leaf, Droplet, Wind, Moon, Info } from 'lucide-react';
const CTAButton = ({
  children,
  subtext
}: {
  children: React.ReactNode;
  subtext?: string;
}) => <div className="flex flex-col items-center w-full max-w-xl mx-auto mt-8 mb-6" data-id="element-0">
    <button className="w-full group relative inline-flex items-center justify-center px-8 py-5 text-lg font-bold text-white transition-all duration-200 bg-gradient-to-b from-emerald-400 to-emerald-600 border-b-4 border-emerald-800 rounded-xl hover:from-emerald-300 hover:to-emerald-500 hover:-translate-y-1 active:translate-y-0 active:border-b-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 focus:ring-offset-dark-900 shadow-[0_0_40px_rgba(16,185,129,0.3)] hover:shadow-[0_0_60px_rgba(16,185,129,0.5)]" data-id="element-1">
      <span className="flex items-center gap-2 text-xl tracking-wide uppercase text-shadow-sm" data-id="element-2">
        {children}
      </span>
    </button>
    {subtext && <p className="mt-3 text-sm font-medium text-gray-400 flex items-center gap-2 text-center" data-id="element-3">
        {subtext}
      </p>}
  </div>;
export function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const carouselSlides = [{
    title: "L'arte dell'idratazione cellulare",
    icon: Droplet
  }, {
    title: "Routine Mattutina: 5 minuti per l'energia",
    icon: Zap
  }, {
    title: "Integrazione: La sinergia d'oro (D3+K2)",
    icon: Activity
  }, {
    title: 'Biohacking del Freddo: La doccia rigenerante',
    icon: Wind
  }, {
    title: 'Mappe Mentali del Sonno',
    icon: Moon
  }, {
    title: 'Respirazione per la Longevità',
    icon: Leaf
  }];
  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % carouselSlides.length);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + carouselSlides.length) % carouselSlides.length);
  const faqs = [{
    q: 'Come ricevo il materiale?',
    a: 'Subito dopo il pagamento riceverai una mail con il link per scaricare tutti i file in formato PDF, pronti per essere letti su cellulare, tablet o stampati.'
  }, {
    q: 'È adatto a chi parte da zero?',
    a: 'Sì, il sistema è disegnato per chiunque voglia migliorare, spiegando concetti complessi in modo semplice e applicabile immediatamente.'
  }, {
    q: 'Le schede sono consultabili dal cellulare?',
    a: 'Assolutamente sì. Tutto il materiale è ottimizzato per dispositivi digitali per permetterti di avere i protocolli sempre con te.'
  }, {
    q: 'La metodologia è aggiornata alle ultime scoperte?',
    a: 'Certamente. Il protocollo RECURSOPACK™ si basa sulle più recenti evidenze scientifiche del 2025/2026 sul biohacking e la longevità.'
  }];
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  return <div className="min-h-screen bg-dark-900 text-gray-300 selection:bg-gold-500 selection:text-black pb-20 overflow-x-hidden" data-id="element-4">
      {/* 1. Attention Bar */}
      <div className="w-full bg-dark-800 border-b border-white/5 py-3 px-4 flex justify-center items-center z-50" data-id="element-5">
        <div className="flex items-center gap-3 max-w-4xl mx-auto text-center" data-id="element-6">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse-fast shrink-0 shadow-[0_0_10px_rgba(239,68,68,0.8)]" data-id="element-7"></div>
          <p className="text-xs md:text-sm font-semibold tracking-wider text-gray-300 uppercase" data-id="element-8">
            ATTENZIONE: UOMINI E DONNE CHE VOGLIONO FERMARE L'INVECCHIAMENTO E
            RECUPERARE LA VITALITÀ DI 10 ANNI FA
          </p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20" data-id="element-9">
        {/* 2. Hero Section */}
        <div className="text-center space-y-8 mb-12" data-id="element-10">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white leading-[1.1] tracking-tight" data-id="element-11">
            Trasforma la tua biologia e spegni l'infiammazione in soli{' '}
            <span className="gold-gradient-text" data-id="element-12">5 minuti al giorno</span>.
          </h1>

          <div className="max-w-3xl mx-auto space-y-6" data-id="element-13">
            <div className="relative pl-6 py-2 text-left" data-id="element-14">
              <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-gold-400 to-gold-600 rounded-full" data-id="element-15"></div>
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed font-medium" data-id="element-16">
                Il primo Sistema{' '}
                <span className="text-white font-bold" data-id="element-17">"Apri e Rigenera"</span>{' '}
                con protocolli scientifici pronti all'uso, dalla nutrizione al
                sonno profondo. Recupera la tua energia vitale e stupisci te
                stesso davanti allo specchio.
              </p>
            </div>
            <p className="text-xl md:text-2xl font-bold text-gold-400" data-id="element-18">
              Il Sistema Longevità Totale — Kit d'Élite Completo
            </p>
          </div>
        </div>

        {/* 3. Product Image & 4. Social Proof */}
        <div className="flex flex-col items-center mb-16" data-id="element-19">
          <div className="relative w-full max-w-3xl mx-auto mb-8" data-id="element-20">
            <div className="absolute inset-0 bg-gold-500/10 blur-[100px] rounded-full pointer-events-none" data-id="element-21"></div>
            <img src="https://cdn.magicpatterns.com/uploads/sj5fv4iM73DnGDjfYAQGid/mocap_sin_fondo.webp" alt="Il Sistema Longevità Totale Bundle" className="relative z-10 w-full h-auto drop-shadow-2xl hover:scale-[1.02] transition-transform duration-500" data-id="element-22" />
          </div>

          <div className="flex flex-col items-center space-y-3 mb-8" data-id="element-23">
            <div className="flex items-center gap-1 text-gold-400" data-id="element-24">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-current" data-id="element-25" />)}
            </div>
            <p className="text-white font-semibold text-lg" data-id="element-26">
              4.9/5 (+1.200 Recensioni Verificate)
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 font-medium text-sm text-center" data-id="element-27">
              <Flame className="w-4 h-4 shrink-0" data-id="element-28" />
              67 persone hanno scelto questo Sistema oggi
            </div>
          </div>

          {/* 5. First CTA Button */}
          <CTAButton subtext="⚡ Accesso Immediato • 🛡️ Garanzia 7 Giorni • 💳 Pagamento Unico" data-id="element-29">
            🚀 SÌ! VOGLIO RIGENERARE IL MIO CORPO
          </CTAButton>

          {/* 6. European flags trust strip */}
          <div className="mt-6 flex flex-col items-center gap-3 text-center" data-id="element-30">
            <div className="flex gap-2 text-2xl" data-id="element-31">🇮🇹 🇨🇭 🇪🇸 🇫🇷 🇩🇪</div>
            <p className="text-sm text-gray-400 max-w-md" data-id="element-32">
              La risorsa N.1 scelta da oltre 15.000 persone in tutta Europa.
            </p>
          </div>
        </div>

        {/* 7. Persuasion paragraph */}
        <div className="mb-20" data-id="element-33">
          <div className="glass-panel p-8 md:p-10 rounded-2xl border-gold-500/20 relative overflow-hidden" data-id="element-34">
            <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-gold-400 to-gold-600" data-id="element-35"></div>
            <Quote className="absolute top-6 right-6 w-24 h-24 text-white/5 rotate-180 pointer-events-none" data-id="element-36" />
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed relative z-10 font-medium" data-id="element-37">
              <span className="text-white font-bold" data-id="element-38">
                Cercare di capire come restare giovani da soli
              </span>{' '}
              ti toglie in media da 2 a 4 ore a settimana tra ricerche confuse
              su internet. Sono{' '}
              <span className="text-gold-400 font-bold" data-id="element-39">
                più di 100 ore all'anno perse
              </span>{' '}
              dietro a consigli spesso sbagliati.
            </p>
          </div>
        </div>

        {/* 8. Comparison Table */}
        <div className="mb-20" data-id="element-40">
          <h2 className="text-3xl font-bold text-center text-white mb-10" data-id="element-41">
            La tua trasformazione
          </h2>
          <div className="glass-panel rounded-2xl overflow-hidden" data-id="element-42">
            <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-43">
              {/* Without System */}
              <div className="p-6 md:p-8 bg-dark-800/50 border-b md:border-b-0 md:border-r border-white/5" data-id="element-44">
                <h3 className="text-xl font-bold text-gray-400 mb-6 flex items-center gap-2" data-id="element-45">
                  SENZA IL SISTEMA
                  <span className="text-sm font-normal text-gray-500 block mt-1" data-id="element-46">
                    (Cosa ti costa oggi)
                  </span>
                </h3>
                <ul className="space-y-6" data-id="element-47">
                  {['Notti insonni o risvegli traumatici senza energia.', 'Stress costante e nebbia mentale durante il lavoro.', 'Infiammazione e dolori che rallentano le tue giornate.', 'Frustrazione per un corpo che invecchia precocemente.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-400" data-id="element-48">
                      <div className="mt-1 bg-red-500/10 p-1 rounded-full shrink-0" data-id="element-49">
                        <X className="w-4 h-4 text-red-500" data-id="element-50" />
                      </div>
                      <span className="leading-relaxed" data-id="element-51">{item}</span>
                    </li>)}
                </ul>
              </div>

              {/* With System */}
              <div className="p-6 md:p-8 bg-emerald-900/10 relative overflow-hidden" data-id="element-52">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 blur-[80px] rounded-full pointer-events-none" data-id="element-53"></div>
                <h3 className="text-xl font-bold text-emerald-400 mb-6 flex items-center gap-2 relative z-10" data-id="element-54">
                  CON IL SISTEMA
                  <span className="text-sm font-normal text-emerald-500/70 block mt-1" data-id="element-55">
                    (La tua vita domani)
                  </span>
                </h3>
                <ul className="space-y-6 relative z-10" data-id="element-56">
                  {['Sonno profondo e risveglio rigenerante istantaneo.', 'Focus laser e chiarezza mentale totale in 2 clic.', "Corpo leggero, flessibile e sistema immunitario d'acciaio.", 'Protocolli anti-aging chiari dalla cellula alla pelle.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-white font-medium" data-id="element-57">
                      <div className="mt-1 bg-emerald-500/20 p-1 rounded-full shrink-0" data-id="element-58">
                        <Check className="w-4 h-4 text-emerald-400" data-id="element-59" />
                      </div>
                      <span className="leading-relaxed" data-id="element-60">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 9. Questa risorsa è per te se... */}
        <div className="mb-20" data-id="element-61">
          <h2 className="text-3xl font-bold text-center text-white mb-10" data-id="element-62">
            Questa risorsa è per te se...
          </h2>
          <div className="glass-panel p-8 rounded-2xl border-white/10" data-id="element-63">
            <ul className="space-y-5" data-id="element-64">
              {['Sei stanco di sacrificare la tua salute per il lavoro e sentirti sempre spossato.', 'Arrivi a casa esausto e non hai più energia per la tua vita privata.', 'Senti che la tua memoria e il tuo metabolismo stanno rallentando ogni anno.', 'Vuoi presentarti al mondo con un aspetto sano, atletico e di prestigio.'].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-65">
                  <div className="mt-1 bg-gold-500/20 p-1.5 rounded-full shrink-0" data-id="element-66">
                    <Check className="w-5 h-5 text-gold-400" data-id="element-67" />
                  </div>
                  <span className="text-lg text-gray-300 leading-relaxed" data-id="element-68">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>

        {/* 10. Cosa otterrai con questo Kit */}
        <div className="mb-20" data-id="element-69">
          <h2 className="text-3xl font-bold text-center text-white mb-12" data-id="element-70">
            Cosa otterrai con questo Kit di Longevità:
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-71">
            {[{
            icon: Zap,
            title: "Protocolli Pronti all'Uso",
            desc: 'Istruzioni passo-passo per ottimizzare la tua biologia in pochi minuti, senza teorie complicate.',
            color: 'text-amber-400',
            bg: 'bg-amber-400/10'
          }, {
            icon: Trophy,
            title: 'Risultati a Basso Costo',
            desc: 'Strategie basate su abitudini naturali e integratori essenziali che trovi ovunque, senza macchinari costosi.',
            color: 'text-emerald-400',
            bg: 'bg-emerald-400/10'
          }, {
            icon: Smartphone,
            title: 'Supporto Visivo Scientifico',
            desc: "Schede ed infografiche già pronte per capire esattamente cosa accade all'interno del tuo corpo.",
            color: 'text-blue-400',
            bg: 'bg-blue-400/10'
          }, {
            icon: Target,
            title: "Metodologia d'Eccellenza",
            desc: 'Un sistema che applica la metodologia avanzata di biohacking certificata da RECURSOPACK™.',
            color: 'text-purple-400',
            bg: 'bg-purple-400/10'
          }].map((item, i) => <div key={i} className="glass-panel p-6 rounded-2xl border-white/5 hover:border-white/10 transition-colors" data-id="element-72">
                <div className={`w-12 h-12 rounded-xl ${item.bg} flex items-center justify-center mb-4`} data-id="element-73">
                  <item.icon className={`w-6 h-6 ${item.color}`} data-id="element-74" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" data-id="element-75">
                  {item.title}
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-76">{item.desc}</p>
              </div>)}
          </div>
        </div>

        {/* 11. Cosa include l'Archivio */}
        <div className="mb-20" data-id="element-77">
          <div className="text-center mb-12" data-id="element-78">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-79">
              Cosa include l'Archivio <br data-id="element-80" />
              <span className="gold-gradient-text" data-id="element-81">
                "Il Sistema Longevità Totale"
              </span>
              ?
            </h2>
            <p className="text-gray-400 text-lg" data-id="element-82">
              Strumenti digitali in PDF pronti da stampare o consultare sul tuo
              tablet da oggi stesso.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-83">
            {[{
            icon: Leaf,
            num: '1',
            title: 'Nutrizione Evoluta 2.0',
            desc: "La scienza per spegnere l'infiammazione silente. Impara a gestire le sirtuine e la flessibilità metabolica per trasformare il tuo cibo in una medicina rigenerativa."
          }, {
            icon: Moon,
            num: '2',
            title: 'Ottimizzazione del Sonno',
            desc: 'Il blueprint per dominare il ritmo circadiano. Architettura della camera da letto, gestione della luce blu e la routine 3-2-1 per un sonno profondo e riparatore.'
          }, {
            icon: Wind,
            num: '3',
            title: 'Biohacking Fisico',
            desc: 'Forza, flessibilità e recupero. Protocolli di esposizione al freddo (sauna/ghiaccio), respirazione avanzata e allenamento ad alto impatto per densità ossea e VO2 max.'
          }, {
            icon: Brain,
            num: '4',
            title: 'Mente Potenziata',
            desc: 'Elimina il brain fog e potenzia la neuroplasticità. Strategie per il Deep Work, gestione della dopamina e nootropi naturali per un focus laser costante.'
          }, {
            icon: Activity,
            num: '5',
            title: 'Guida ai Supersupplementi',
            desc: "La mappa definitiva dell'integrazione di lusso. Vitamina D3+K2, Omega-3, NMN, Resveratrolo e Glutatione. Sapere esattamente cosa prendere e quando."
          }, {
            icon: Zap,
            num: '6',
            title: 'La Routine del Centenario',
            desc: "Il sistema quotidiano completo. Dalla luce solare del mattino al detox digitale serale. Un piano d'azione di 30 giorni per installare abitudini di vita eterna."
          }].map((item, i) => <div key={i} className="glass-panel p-8 rounded-2xl relative overflow-hidden group" data-id="element-84">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gold-500/5 rounded-full blur-3xl group-hover:bg-gold-500/10 transition-colors" data-id="element-85"></div>
                <div className="flex items-center gap-4 mb-5 relative z-10" data-id="element-86">
                  <div className="w-14 h-14 rounded-xl bg-dark-800 border border-white/10 flex items-center justify-center shrink-0" data-id="element-87">
                    <item.icon className="w-7 h-7 text-gold-400" data-id="element-88" />
                  </div>
                  <span className="text-xs font-bold text-gold-400 uppercase tracking-widest bg-gold-500/10 px-3 py-1 rounded-full border border-gold-500/20" data-id="element-89">
                    Tomo {item.num}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3 relative z-10" data-id="element-90">
                  {item.title}
                </h3>
                <p className="text-gray-400 leading-relaxed relative z-10 text-sm" data-id="element-91">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>

        {/* 12. Image Carousel */}
        <div className="mb-24" data-id="element-92">
          <div className="text-center mb-10" data-id="element-93">
            <h2 className="text-3xl font-bold text-white mb-2" data-id="element-94">
              Sfoglia il materiale all'interno
            </h2>
            <p className="text-gold-400 font-medium" data-id="element-95">
              {currentSlide + 1} / {carouselSlides.length}
            </p>
          </div>

          <div className="relative max-w-2xl mx-auto" data-id="element-96">
            {/* Navigation Buttons */}
            <button onClick={prevSlide} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 w-12 h-12 rounded-full bg-dark-800 border border-white/10 flex items-center justify-center text-white hover:bg-dark-700 hover:text-gold-400 transition-colors z-20 shadow-xl" data-id="element-97">
              <ChevronLeft className="w-6 h-6" data-id="element-98" />
            </button>

            <button onClick={nextSlide} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 w-12 h-12 rounded-full bg-dark-800 border border-white/10 flex items-center justify-center text-white hover:bg-dark-700 hover:text-gold-400 transition-colors z-20 shadow-xl" data-id="element-99">
              <ChevronRight className="w-6 h-6" data-id="element-100" />
            </button>

            {/* Carousel Track */}
            <div className="overflow-hidden rounded-2xl glass-panel border-gold-500/20 aspect-video relative" data-id="element-101">
              <div className="flex h-full transition-transform duration-500 ease-out" style={{
              transform: `translateX(-${currentSlide * 100}%)`
            }} data-id="element-102">
                {carouselSlides.map((slide, i) => {
                const Icon = slide.icon;
                return <div key={i} className="min-w-full h-full flex flex-col items-center justify-center p-8 relative" data-id="element-103">
                      <div className="absolute inset-0 bg-gradient-to-br from-dark-800 to-dark-900 opacity-80" data-id="element-104"></div>
                      <div className="absolute top-0 right-0 w-64 h-64 bg-gold-500/10 blur-[80px] rounded-full" data-id="element-105"></div>

                      <div className="relative z-10 flex flex-col items-center text-center space-y-6" data-id="element-106">
                        <div className="w-20 h-20 rounded-2xl bg-gold-500/10 border border-gold-500/20 flex items-center justify-center shadow-[0_0_30px_rgba(251,191,36,0.15)]" data-id="element-107">
                          <Icon className="w-10 h-10 text-gold-400" data-id="element-108" />
                        </div>
                        <h3 className="text-2xl md:text-3xl font-bold text-white max-w-md leading-tight" data-id="element-109">
                          {slide.title}
                        </h3>
                        <div className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-gray-400 font-medium" data-id="element-110">
                          Anteprima Documento
                        </div>
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </div>

        {/* 13. Bonuses Section */}
        <div className="mb-24 relative" data-id="element-111">
          <div className="absolute inset-0 bg-gold-500/5 blur-[120px] rounded-full pointer-events-none" data-id="element-112"></div>
          <div className="glass-panel border-gold-500/20 p-8 md:p-12 rounded-3xl relative z-10" data-id="element-113">
            <div className="text-center mb-12" data-id="element-114">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-115">
                6 Bonus Esclusivi Inclusi
              </h2>
              <p className="text-gold-400 font-medium text-lg uppercase tracking-wider" data-id="element-116">
                (Senza costi extra)
              </p>
              <div className="inline-block mt-4 px-6 py-2 rounded-full bg-dark-800 border border-white/10" data-id="element-117">
                <p className="text-gray-300 font-semibold" data-id="element-118">
                  Valore totale dei bonus:{' '}
                  <span className="text-white" data-id="element-119">€ 49</span>
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-120">
              {[{
              icon: Flame,
              title: 'Lista della Spesa Strategica',
              desc: 'Guida ai super-alimenti nei portali italiani (Esselunga, Conad, Coop) per una spesa perfetta.'
            }, {
              icon: Calendar,
              title: 'Guida ai Test di Laboratorio',
              desc: 'Il planner definitivo per sapere quali analisi chiedere al tuo medico e come leggerle come un esperto.'
            }, {
              icon: Palette,
              title: 'Guida al Digiuno Intermittente',
              desc: "Fiche didattiche sui protocolli più potenti per attivare l'autofagia e pulire le tue cellule."
            }, {
              icon: Search,
              title: 'Protocollo Anti-Infiammatorio Rapido',
              desc: '10 progetti alimentari ed abitudini per spegnere il fuoco interno in meno di una settimana.'
            }, {
              icon: Trophy,
              title: 'Longevity Kitchen Tools',
              desc: 'La lista degli strumenti tech essenziali per trasformare la tua cucina in un laboratorio di salute.'
            }, {
              icon: Edit3,
              title: 'Il Tracker delle Abitudini',
              desc: 'Il diario suggestivo per tracciare ogni giorno i tuoi progressi e immergerti nella tua nuova routine.'
            }].map((bonus, i) => <div key={i} className="flex flex-col sm:flex-row items-start gap-5 p-6 rounded-2xl bg-dark-800/80 border border-white/5 hover:border-gold-500/30 transition-colors" data-id="element-121">
                  <div className="w-12 h-12 rounded-xl bg-gold-500/10 flex items-center justify-center shrink-0 border border-gold-500/20" data-id="element-122">
                    <bonus.icon className="w-6 h-6 text-gold-400" data-id="element-123" />
                  </div>
                  <div className="flex-1" data-id="element-124">
                    <div className="flex items-center justify-between gap-2 mb-2" data-id="element-125">
                      <h4 className="text-white font-bold text-lg leading-tight" data-id="element-126">
                        {bonus.title}
                      </h4>
                      <span className="px-2.5 py-1 text-[10px] font-black bg-emerald-500/20 text-emerald-400 rounded uppercase tracking-wider shrink-0" data-id="element-127">
                        Gratis
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm leading-relaxed" data-id="element-128">
                      {bonus.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* 14. Pricing Section */}
        <div className="mb-16 text-center" data-id="element-129">
          <h2 className="text-3xl font-bold text-white mb-10" data-id="element-130">
            L'Offerta "Longevità Totale"
          </h2>

          <div className="inline-block w-full max-w-2xl glass-panel p-8 md:p-12 rounded-3xl border-gold-500/40 relative overflow-hidden text-left shadow-[0_0_50px_rgba(251,191,36,0.05)]" data-id="element-131">
            <div className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold px-12 py-2 rotate-45 translate-x-10 translate-y-6 shadow-lg z-20 tracking-wider" data-id="element-132">
              92% OFF
            </div>

            <div className="flex justify-center mb-8" data-id="element-133">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold-500/20 border border-gold-500/30 text-gold-400 text-sm font-bold tracking-wide uppercase" data-id="element-134">
                <Star className="w-4 h-4 fill-current" data-id="element-135" />
                LA SCELTA PIÙ POPOLARE
              </div>
            </div>

            <p className="text-gray-400 font-bold uppercase tracking-widest text-sm mb-6 border-b border-white/10 pb-4" data-id="element-136">
              VALORE TOTALE DEI CONTENUTI:
            </p>

            <ul className="space-y-4 mb-8" data-id="element-137">
              {[{
              name: 'Percorso Longevità (6 Tomi Completi)',
              price: '€ 97'
            }, {
              name: 'Registro Biomarcatori & Materiale Visuale',
              price: '€ 57'
            }, {
              name: 'Manuali Tecnici di Biohacking',
              price: '€ 47'
            }, {
              name: '6 Bonus Esclusivi',
              price: '€ 49'
            }].map((item, i) => <li key={i} className="flex justify-between items-center text-gray-300" data-id="element-138">
                  <span className="flex items-center gap-2" data-id="element-139">
                    <Check className="w-4 h-4 text-emerald-500" data-id="element-140" />
                    {item.name}
                  </span>
                  <span className="font-medium" data-id="element-141">{item.price}</span>
                </li>)}
            </ul>

            <div className="border-t border-white/10 pt-6 mb-8 flex justify-between items-center" data-id="element-142">
              <span className="text-gray-400 font-medium" data-id="element-143">VALORE REALE:</span>
              <span className="text-xl text-gray-500 line-through decoration-red-500/50 decoration-2 font-bold" data-id="element-144">
                € 250,00
              </span>
            </div>

            <div className="text-center space-y-2 mb-8 bg-dark-900/50 p-6 rounded-2xl border border-white/5" data-id="element-145">
              <p className="text-gold-400 font-bold tracking-widest uppercase text-sm" data-id="element-146">
                SOLO OGGI
              </p>
              <div className="flex items-start justify-center gap-1" data-id="element-147">
                <span className="text-3xl font-bold text-white mt-2" data-id="element-148">€</span>
                <span className="text-7xl font-black text-white tracking-tighter" data-id="element-149">
                  19
                </span>
                <span className="text-3xl font-bold text-white mt-2" data-id="element-150">,90</span>
              </div>
            </div>

            <div className="flex flex-col items-center gap-2 text-sm text-gray-400 font-medium mb-8 text-center" data-id="element-151">
              <p data-id="element-152">💳 Pagamento unico • ⚡ Accesso immediato</p>
              <p className="text-red-400 flex items-center gap-1" data-id="element-153">
                <Clock className="w-4 h-4" data-id="element-154" /> Prezzo speciale di lancio — salirà
                a breve
              </p>
            </div>

            {/* 15. Second CTA */}
            <button className="w-full group relative inline-flex items-center justify-center px-8 py-5 text-lg font-bold text-white transition-all duration-200 bg-gradient-to-b from-emerald-400 to-emerald-600 border-b-4 border-emerald-800 rounded-xl hover:from-emerald-300 hover:to-emerald-500 hover:-translate-y-1 active:translate-y-0 active:border-b-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 focus:ring-offset-dark-900 shadow-[0_0_40px_rgba(16,185,129,0.3)] hover:shadow-[0_0_60px_rgba(16,185,129,0.5)]" data-id="element-155">
              <span className="flex items-center gap-2 text-xl tracking-wide uppercase text-shadow-sm" data-id="element-156">
                🚀 ACQUISTA ORA — € 19,90
              </span>
            </button>
            <p className="mt-4 text-sm text-center text-gray-400 font-medium" data-id="element-157">
              ⚡ Accesso Immediato • 💳 Un solo Pagamento • 🛡️ Garanzia di 7
              Giorni
            </p>
          </div>
        </div>

        {/* 16. Guarantee Section */}
        <div className="max-w-3xl mx-auto glass-panel p-8 md:p-10 rounded-3xl flex flex-col md:flex-row items-center gap-8 text-center md:text-left border-emerald-500/30 mb-20 relative overflow-hidden" data-id="element-158">
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" data-id="element-159"></div>
          <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0 border border-emerald-500/20 relative z-10" data-id="element-160">
            <ShieldCheck className="w-10 h-10 text-emerald-400" data-id="element-161" />
          </div>
          <div className="relative z-10" data-id="element-162">
            <h3 className="text-2xl font-bold text-white mb-3" data-id="element-163">
              RECUPERA LA TUA VITALITÀ O TI RIMBORSIAMO
            </h3>
            <p className="text-gray-300 text-base leading-relaxed" data-id="element-164">
              Siamo così sicuri dell'efficacia del SISTEMA LONGEVITÀ TOTALE che
              ti offriamo 7 giorni per provarlo. Se non senti che il tuo corpo è
              rinvigorito e la tua mente più lucida, ti restituiremo ogni
              centesimo. Senza domande.
            </p>
          </div>
        </div>

        {/* 17. About RECURSOPACK */}
        <div className="max-w-3xl mx-auto mb-20 text-center" data-id="element-165">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-dark-800 border border-white/10 mb-6" data-id="element-166">
            <Palette className="w-8 h-8 text-gray-400" data-id="element-167" />
          </div>
          <h3 className="text-xl font-bold text-white mb-6 uppercase tracking-widest" data-id="element-168">
            A proposito di RECURSOPACK™
          </h3>
          <p className="text-xl md:text-2xl text-gray-400 italic leading-relaxed font-serif" data-id="element-169">
            "Abbiamo creato questo sistema per trasformare la tua biologia in
            una fonte di energia inesauribile, togliendo il peso della ricerca
            confusionaria e della burocrazia medica. Meno invecchiamento, più
            vita."
          </p>
        </div>

        {/* 18. FAQ Section */}
        <div className="max-w-3xl mx-auto mb-20" data-id="element-170">
          <h2 className="text-3xl font-bold text-center text-white mb-10" data-id="element-171">
            Domande Frequenti
          </h2>
          <div className="space-y-4" data-id="element-172">
            {faqs.map((faq, index) => <div key={index} className="glass-panel rounded-xl border-white/5 overflow-hidden transition-all duration-300" data-id="element-173">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none hover:bg-white/[0.02] transition-colors" data-id="element-174">
                  <span className="text-lg font-semibold text-white pr-4" data-id="element-175">
                    {faq.q}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-gold-400 shrink-0 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`} data-id="element-176" />
                </button>
                <div className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${openFaq === index ? 'max-h-40 pb-5 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-177">
                  <p className="text-gray-400 leading-relaxed border-t border-white/5 pt-4" data-id="element-178">
                    {faq.a}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* 19. Final CTA */}
        <div className="mb-10" data-id="element-179">
          <CTAButton subtext="⚡ Accesso Immediato • 🛡️ Garanzia di 7 Giorni" data-id="element-180">
            💪 SÌ! VOGLIO RIVOLUZIONARE LA MIA SALUTE ORA
          </CTAButton>
        </div>
      </main>

      {/* 20. Footer */}
      <footer className="mt-12 border-t border-white/5 pt-12 pb-8 px-4 text-center" data-id="element-181">
        <div className="max-w-2xl mx-auto space-y-6" data-id="element-182">
          <div className="flex items-center justify-center gap-2 text-gray-400 font-bold text-lg" data-id="element-183">
            <Palette className="w-5 h-5" data-id="element-184" /> RECURSOPACK™ Italia
          </div>
          <p className="text-gray-500 text-sm leading-relaxed" data-id="element-185">
            Risorse d'eccellenza per la tua biologia superiore. Perché vivere al
            massimo non dovrebbe toglierti i tuoi momenti di relax.
          </p>
          <p className="text-gray-600 text-xs pt-4" data-id="element-186">
            © RECURSOPACK™ 2026 — Tutti i diritti riservati.
          </p>
        </div>
      </footer>
    </div>;
}